import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import { getBaseUrl } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { getPathNameFromSlug } from '@/utils/path/getPathNameFromSlug'
import { generateSeoUrl } from '@/utils/seo/generateSeoUrl'
import { useUrl } from '@/utils/url/useUrl'

import { useEntry } from '../Theme.hooks'

type Props = {
	children: React.ReactNode
}

export function ThemeMeta({ children }: Props) {
	const entry = useEntry()
	const {
		query: { slug },
		locale = 'en-US'
	} = useRouter()

	const path = getPathNameFromSlug(slug)

	const url = useUrl({ locale, path: `theme/${path}` })

	if ('error' in entry) return <>{children}</>

	const seoMetadata = entry.data?.seoMetadata

	const title = seoMetadata?.title?.endsWith('| BigCommerce')
		? seoMetadata.title
		: `${seoMetadata?.title} | BigCommerce`

	const description =
		seoMetadata?.description ??
		'Learn how BigCommerce can fuel your business with all the capabilities of enterprise ecommerce—without the cost or complexity.'

	return (
		<>
			<NextSeo
				title={title}
				description={description}
				canonical={seoMetadata?.canonicalUrl || generateSeoUrl(url?.href)}
				languageAlternates={[
					{ hrefLang: 'en-au', href: `${getBaseUrl('en-AU')}/theme/${slug}/` },
					{ hrefLang: 'en-gb', href: `${getBaseUrl('en-GB')}/theme/${slug}/` },
					{ hrefLang: 'en-us', href: `${getBaseUrl('en-US')}/theme/${slug}/` },
					{ hrefLang: 'x-default', href: `${getBaseUrl()}/theme/${slug}/` }
				]}
				openGraph={{
					title,
					description: description,
					url: generateSeoUrl(url?.href),
					siteName: 'BigCommerce',
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata?.image?.url,
									width: seoMetadata?.image?.width,
									height: seoMetadata?.image?.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale),
					article: {
						publishedTime: entry?.data?.publishedDate
							? new Date(entry.data.publishedDate).toISOString()
							: new Date()?.toISOString()
					}
				}}
				nofollow={Boolean(seoMetadata?.nofollow)}
				noindex={Boolean(seoMetadata?.noindex)}
			/>
			{children}
		</>
	)
}
